import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ADMIN } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'fitur_admin_token';

const objectiveLabels = {
    Escalar: 'Escalar Mercados',
    Innovacion: 'Innovación & Tech',
    Aprender: 'Aprender de Referentes',
    Red: 'Fortalecer la Red',
};

const budgetLabels = {
    '<2k': 'Menos de USD 2.000',
    '2-4.5k': 'USD 2.000 – 4.500',
    '4.5-6k': 'USD 4.500 – 6.000',
    '>6k': 'Más de USD 6.000',
    '<3k': 'Menos de USD 3.000',
    '3-5k': 'USD 3.000 – 5.000',
    '5-10k': 'USD 5.000 – 10.000',
    '>10k': 'Más de USD 10.000',
};

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });
};

const AdminPage = () => {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [user, setUser] = useState(null);
    const [leads, setLeads] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
        setLeads(null);
    }, []);

    const loadLeads = useCallback(async (tok) => {
        try {
            const { data } = await axios.get(`${API}/admin/leads`, {
                headers: { Authorization: `Bearer ${tok}` },
            });
            setLeads(data.leads);
        } catch (e) {
            if (e?.response?.status === 401) {
                logout();
            } else {
                setError('No pudimos cargar los leads. Intenta de nuevo.');
            }
        }
    }, [logout]);

    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(data);
                loadLeads(token);
            } catch {
                logout();
            }
        })();
    }, [token, loadLeads, logout]);

    const submitLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            localStorage.setItem(TOKEN_KEY, data.token);
            setUser({ email: data.email, name: data.name });
            setToken(data.token);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'No pudimos iniciar sesión. Intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    if (!token || !user) {
        return (
            <div className="min-h-screen bg-[color:var(--bg)] flex items-center justify-center px-6">
                <form
                    data-testid={ADMIN.loginForm}
                    onSubmit={submitLogin}
                    className="w-full max-w-md border border-white/12 bg-[color:var(--surface)] p-8 md:p-10"
                >
                    <div className="eyebrow-brand mb-4">Acceso restringido</div>
                    <h1 className="font-display text-white text-3xl mb-8">Panel de administración</h1>
                    <label className="block mb-5">
                        <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white/60 mb-2 block">Correo</span>
                        <input
                            data-testid={ADMIN.loginEmail}
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-transparent border-b border-white/25 focus:border-[color:var(--brand)] outline-none py-3 text-white placeholder:text-white/35 transition-colors"
                            placeholder="tu correo"
                        />
                    </label>
                    <label className="block mb-6">
                        <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white/60 mb-2 block">Contraseña</span>
                        <input
                            data-testid={ADMIN.loginPassword}
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-transparent border-b border-white/25 focus:border-[color:var(--brand)] outline-none py-3 text-white placeholder:text-white/35 transition-colors"
                            placeholder="••••••••"
                        />
                    </label>
                    {error && (
                        <p data-testid={ADMIN.loginError} className="mb-5 text-sm text-[color:var(--brand)]">{error}</p>
                    )}
                    <button
                        data-testid={ADMIN.loginSubmit}
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full justify-center disabled:opacity-60"
                    >
                        {loading ? 'Entrando…' : 'Entrar al panel'}
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div data-testid={ADMIN.panel} className="min-h-screen bg-[color:var(--bg)] text-white">
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                    <div>
                        <div className="eyebrow-brand mb-3">Panel de administración</div>
                        <h1 className="font-display text-3xl md:text-5xl">
                            Leads de la <span className="italic-accent text-[color:var(--brand)] font-normal">Misión FITUR 2027</span>
                        </h1>
                        <p className="mt-3 text-white/60 text-sm">
                            Hola, {user.name} · <span data-testid={ADMIN.leadsCount}>{leads ? `${leads.length} registrados` : 'cargando…'}</span>
                        </p>
                    </div>
                    <button
                        data-testid={ADMIN.logoutButton}
                        onClick={logout}
                        className="self-start md:self-auto rounded-full border border-white/25 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-white/70 hover:border-[color:var(--brand)] hover:text-white transition-colors"
                    >
                        Cerrar sesión
                    </button>
                </div>

                {error && <p className="mb-6 text-sm text-[color:var(--brand)]">{error}</p>}

                <div className="border border-white/12 overflow-x-auto">
                    <table data-testid={ADMIN.leadsTable} className="w-full min-w-[1100px] text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-white/50 border-b border-white/12">
                                <th className="px-4 py-4">Fecha</th>
                                <th className="px-4 py-4">Nombre</th>
                                <th className="px-4 py-4">Empresa</th>
                                <th className="px-4 py-4">Email</th>
                                <th className="px-4 py-4">WhatsApp</th>
                                <th className="px-4 py-4">País</th>
                                <th className="px-4 py-4">Objetivo</th>
                                <th className="px-4 py-4">FITUR previo</th>
                                <th className="px-4 py-4">Presupuesto</th>
                                <th className="px-4 py-4">Notas</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(leads || []).map((l) => (
                                <tr key={l.id} className="border-b border-white/8 hover:bg-white/[0.03] transition-colors">
                                    <td className="px-4 py-4 text-white/60 whitespace-nowrap">{formatDate(l.created_at)}</td>
                                    <td className="px-4 py-4 font-semibold">{l.full_name}</td>
                                    <td className="px-4 py-4">{l.company}</td>
                                    <td className="px-4 py-4 text-white/80">{l.email}</td>
                                    <td className="px-4 py-4 text-white/80 whitespace-nowrap">{l.whatsapp || '—'}</td>
                                    <td className="px-4 py-4">{l.country}</td>
                                    <td className="px-4 py-4">{objectiveLabels[l.objective] || l.objective}</td>
                                    <td className="px-4 py-4 capitalize">{l.attended_fitur || '—'}</td>
                                    <td className="px-4 py-4 whitespace-nowrap">{budgetLabels[l.budget_range] || '—'}</td>
                                    <td className="px-4 py-4 text-white/60 max-w-[220px] truncate" title={l.notes || ''}>{l.notes || '—'}</td>
                                </tr>
                            ))}
                            {leads && leads.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-4 py-12 text-center text-white/50">
                                        Aún no hay leads registrados.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminPage;
